import React, { useState } from 'react';
import { Dimmer, Loader } from 'semantic-ui-react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated, logout as logoutAction } from '../../redux/slices/authSlice';
import { usePersonImage } from '../../hooks/usePersonImage';
import { enhancedClosetApi } from '../../redux/apis/endpoints/closetApi';
import { enhancedPersonImageApi } from '../../redux/apis/endpoints/personImageApi';
import { enhancedBackgroundImageApi } from '../../redux/apis/endpoints/backgroundImageApi';
import { enhancedVTOResultApi } from '../../redux/apis/endpoints/vtoResultApi';
import VTOLoginConfirmationPortal from '../virtual-try-on/VTOLoginConfirmationPortal';

const AuthButton = ({ className = '', useDimmer = false }) => {
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const dispatch = useDispatch();

  const { clearPersonImage } = usePersonImage();

  const isAuthenticated = useSelector(selectIsAuthenticated);

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    window.location.reload();
  };

  const handleLoginModalClose = () => {
    setShowLoginModal(false);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      // Dispatch Redux logout action (clears token from state + localStorage)
      dispatch(logoutAction());

      // Clear person image data
      clearPersonImage();

      // Reset all Redux slices
      dispatch({ type: 'personImage/resetPersonImageState' });
      dispatch({ type: 'background/resetBackgroundState' });
      dispatch({ type: 'vto/resetVTOState' });
      dispatch({ type: 'clothingItems/resetClothingItemsState' });

      // Reset all RTK Query caches
      dispatch(enhancedClosetApi.util.resetApiState());
      dispatch(enhancedPersonImageApi.util.resetApiState());
      dispatch(enhancedBackgroundImageApi.util.resetApiState());
      dispatch(enhancedVTOResultApi.util.resetApiState());

      // Hard reload to clear all in-memory state
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      setIsLoggingOut(false);
    }
  };

  const getButtonState = () => {
    if (isAuthenticated) {
      return {
        text: isLoggingOut && !useDimmer ? 'Logging out...' : 'Logout',
        className: isLoggingOut && !useDimmer ? 'loading' : 'logout',
        title: 'Logout',
        onClick: handleLogout,
        disabled: isLoggingOut
      };
    } else {
      return {
        text: 'Login',
        className: 'login',
        title: 'Login',
        onClick: handleLoginClick,
        disabled: false
      };
    }
  };

  const buttonState = getButtonState();

  return (
    <>
      {useDimmer && isLoggingOut ? (
        <Dimmer active page>
          <Loader>Logging out...</Loader>
        </Dimmer>
      ) : null}
      <div className={`auth-buttons-container ${className}`} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <div className="auth-button">
          <button
            className={`auth-text-btn ${buttonState.className}`}
            onClick={buttonState.onClick}
            disabled={buttonState.disabled}
            title={buttonState.title}
            style={!isAuthenticated ? {
              padding: '6px 30px',
              margin: '0 25px 0 0',
              fontSize: '14px'
            } : {}}
          >
            {buttonState.text}
          </button>
        </div>
      </div>

      {/* Inline Login Modal */}
      <VTOLoginConfirmationPortal
        isOpen={showLoginModal}
        onLogin={handleLoginSuccess}
        onCancel={handleLoginModalClose}
        onClose={handleLoginModalClose}
      />
    </>
  );
};

export default AuthButton;